// frontend/src/components/FileUpload.tsx

import React from 'react';
import { Box, Button, Typography, Snackbar, Alert } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';

import { useFileUpload } from '../hooks/useFileUpload';
import { useDropzoneUpload } from '../hooks/useDropzoneUpload';
import UploadDropzone from './fileUpload/UploadDropzone';
import { FileInfo, UploadLimits } from '../types';

const API_URL = process.env.REACT_APP_API_URL;

const FileUpload: React.FC = () => {
  const { uploadFile, isUploading, snackbar, handleCloseSnackbar, setSnackbar, progress } = useFileUpload();

  const { data: fileListData } = useQuery<FileInfo[]>({
    queryKey: ['files'],
    queryFn: async () => {
      const response = await axios.get(`${API_URL}/api/files/`);
      return response.data;
    },
    retry: false,
  });

  const currentFileCount = fileListData?.length || 0;

  const {
    data: uploadLimits,
    isError: isUploadLimitsError,
    isFetching: isUploadLimitsFetching,
    refetch: refetchUploadLimits,
  } = useQuery<UploadLimits>({
    queryKey: ['uploadLimits'],
    queryFn: async () => {
      const response = await axios.get(`${API_URL}/api/files/upload-limits`);
      return response.data;
    },
  });

  const hasUploadLimits = Boolean(uploadLimits);
  const maxUploadFiles = uploadLimits?.max_upload_files;
  const maxTotalUploadSize = (uploadLimits?.max_total_upload_size_mb ?? 0) * 1024 * 1024;
  const currentTotalUploadSize = (uploadLimits?.current_total_upload_size_mb ?? 0) * 1024 * 1024;
  const remainingUploadSize = hasUploadLimits
    ? Math.max(0, maxTotalUploadSize - currentTotalUploadSize)
    : 0;
  const remainingSlots = hasUploadLimits && maxUploadFiles !== undefined
    ? Math.max(0, maxUploadFiles - currentFileCount)
    : 0;
  const uploadLimitsDisabledReason = isUploadLimitsError
    ? 'アップロード制限を確認できませんでした。再試行してください。'
    : hasUploadLimits
      ? undefined
      : 'アップロード制限を確認しています。';

  const {
    isDragging,
    isPreparing,
    preparationProgress,
    handleDragEnter,
    handleDragLeave,
    handleDragOver,
    handleDrop,
    handleFolderSelect,
    handleFileSelect,
  } = useDropzoneUpload({
    remainingSlots,
    remainingUploadSize,
    uploadLimits,
    isUploading,
    uploadFile,
    setSnackbar,
  });
  const isUploadActive = isUploading || isPreparing;
  const displayedProgress = isPreparing ? preparationProgress : progress;

  return (
    <Box sx={{ mb: 4 }}>
      <Typography variant="h6" gutterBottom>
        データアップロード
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 2, whiteSpace: 'pre-line' }}>
        {`CSVファイルが含まれるフォルダまたはファイルを選択するか、このエリアにドラッグ＆ドロップしてください。
          複数のCSVファイルが格納されたフォルダがアップロードされた場合は、自動で中身のファイルを一つにまとめます。
          ただし、ヘッダ情報が異なるなど、データ形式が不正なファイルはスキップされます。`}
      </Typography>
      {isUploadLimitsError && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => void refetchUploadLimits()}
              disabled={isUploadLimitsFetching}
            >
              再試行
            </Button>
          }
        >
          アップロード制限を取得できませんでした。時間をおいて再試行してください。
        </Alert>
      )}
      <UploadDropzone
        isDragging={isDragging}
        isUploading={isUploadActive}
        remainingSlots={remainingSlots}
        disabledReason={uploadLimitsDisabledReason}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onFolderSelect={handleFolderSelect}
        onFileSelect={handleFileSelect}
        progress={displayedProgress}
      />
      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={handleCloseSnackbar}>
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default FileUpload;
